import React from "react"
import PropTypes from "prop-types"
import { connect, useSelector } from "react-redux"
import { defineMessages, useIntl } from "react-intl"
import { useLocation } from "react-router-dom"
import _ from "lodash"
// Material Ui
import AlertTitle from "@material-ui/lab/AlertTitle"
import Alert from "@material-ui/lab/Alert"
import Box from "@material-ui/core/Box"
import Button from "@material-ui/core/Button"
import Typography from "@material-ui/core/Typography"
// ## //
import getDisplayName from "./Utils"
import widgetController from "../../api/widgetSlice"
import CircularProgressBox from "../CircularProgressBox"
import useEnvironment from "../../hooks/useEnvironment"

const { actions } = widgetController
const { resetCall, clearWidgetMessage } = actions

const messages = defineMessages({
  widgetAPICallError: {
    defaultMessage:
      "One (or several) errors happened while retrieving widget data.",
    id: "widget.api.call.error"
  },
  errorLabel: {
    defaultMessage: "Error",
    id: "common.label.error"
  },
  infoLabel: {
    defaultMessage: "Infos",
    id: "word.infos"
  },
  infoAccountCreateRecently: {
    defaultMessage:
      "Your account was recently created. Your portfolios and your data may not yet be available online.",
    id: "widget.selector.emtpy.accountCreateRecently"
  },
  infoAccountContactUsDetails: {
    defaultMessage:
      "However, if the problem persists please report the issue with the following link.",
    id: "widget.selector.contact.us.details"
  },
  contactUs: {
    defaultMessage: "Contact us",
    id: "widget.selector.contact.us"
  }
})

// see
//  > https://developer.mozilla.org/fr/docs/Web/Guide/HTML/Liens_email
//  > https://mailtolink.me/
function buildURL(user, page, contact) {
  const recipient = contact
  const mailContent = `subject=%5Bwebportal%5D%20-%20Espace%20priv%C3%A9%20sans%20donn%C3%A9es&body=Suite%20%C3%A0%20une%20connexion%20%C3%A0%20mon%20espace%20priv%C3%A9%2C%20les%20pages%20consult%C3%A9es%20sont%20vides.
  %0D%0A
  %0D%0AInformations%3A
  %0D%0ACompte%3A%20${user.primaryEmail}
  %0D%0APage%20consult%C3%A9e%3A%20${page}`
  return `mailto:${recipient}?${mailContent}`
}

const NoDataInfoComponent = () => {
  const location = useLocation()
  const { contact } = useEnvironment()
  const user = useSelector((state) => state.auth.user)
  const { formatMessage: format } = useIntl()
  const mailToHref = buildURL(user, location?.pathname, contact)
  return (
    <Alert severity="info">
      <AlertTitle>{format(messages.infoLabel)}</AlertTitle>
      <Box mt={2}>
        <Typography>{format(messages.infoAccountCreateRecently)}</Typography>
        <Typography>{format(messages.infoAccountContactUsDetails)}</Typography>
      </Box>
      <Box display="flex" justifyContent="flex-end">
        <Button href={mailToHref}>{format(messages.contactUs)}</Button>
      </Box>
    </Alert>
  )
}

const ErrorComponent = () => {
  const intl = useIntl()
  return (
    <Alert severity="error">
      <AlertTitle>{intl.formatMessage(messages.errorLabel)}</AlertTitle>
      {intl.formatMessage(messages.widgetAPICallError)}
    </Alert>
  )
}

/**
 * A High Order component to wrap any page that contains Widgets.
 * It displays a page loader when at least one HTTP request is still pending.
 * It displays global error message when at least one error occurs while processing HTTP request.
 * It can also displays information message when something is misconfigured (ex: no portfolio set for a customer)
 *
 * @param WidgetPageContainer a widget container that will render them
 * @param config a set of props forwrarded to the container
 * @returns {*}
 * @constructor
 */
export default function withWidgets(WidgetPageContainer, config) {
  const wrapper = class extends React.Component {
    constructor(innerProps) {
      super(innerProps)
      this.state = { isComponentHidden: false }
    }

    componentDidMount() {
      const { resetCalls, widgetPreview } = this.props
      if (widgetPreview && resetCalls) {
        resetCalls()
      }
    }

    /**
     * At mounting time, sub components should be hidden,
     * that's why it has a hidden state property init to true.
     * Then, some asynchronous widget call are made, using redux.
     * Finally, update hidden property state when it got response from API call.
     */
    componentDidUpdate() {
      const { widgetCallsOngoing, widgetCallsError } = this.props
      const { isComponentHidden } = this.state

      // all API widget calls are OK
      if (
        widgetCallsOngoing === 0 &&
        widgetCallsError === 0 &&
        isComponentHidden
      ) {
        // eslint-disable-next-line react/no-did-update-set-state
        this.setState({ isComponentHidden: false })
        return
      }

      // some API widgets call are in ERROR, then main Widget components are hidden
      if (
        widgetCallsOngoing === 0 &&
        widgetCallsError > 0 &&
        !isComponentHidden
      ) {
        // eslint-disable-next-line react/no-did-update-set-state
        this.setState({ isComponentHidden: true })
        return
      }

      // some selectors value changed, that triggered new API call
      // then hide sub component again
      if (
        widgetCallsOngoing > 0 &&
        widgetCallsError === 0 &&
        !isComponentHidden
      ) {
        // eslint-disable-next-line react/no-did-update-set-state
        this.setState({ isComponentHidden: true })
      }
    }

    componentWillUnmount() {
      // console.log("[withWidgets] unmount => clearMessage", this.props)
      const { clearMessages } = this.props
      if (clearMessages) {
        clearMessages()
      }
    }

    render() {
      // console.log(
      //   `[withWidgets] render (isHidden = ${this.state.isComponentHidden}):`,
      //   this.props
      // )
      const {
        messageToDisplay,
        widgetCallsOngoing,
        widgetCallsError,
        widgetPreview
      } = this.props
      const { isComponentHidden } = this.state
      // a widget preview does not render real widget
      // it will display skeleton
      if (widgetPreview) {
        return <WidgetPageContainer {...this.props} {...config} />
      }

      // Info: This is made together so that react can properly diff and avoid unmounting/remounting
      // WidgetPageContainer (which in turns trigger data retrieval)
      const mainComponentStyle = isComponentHidden ? { display: "none" } : {}
      return (
        <Box width="100%" overflow="hidden">
          {widgetCallsOngoing > 0 && <CircularProgressBox height="66vh" />}
          {widgetCallsError > 0 && widgetCallsOngoing === 0 && (
            <ErrorComponent />
          )}

          <Box style={mainComponentStyle}>
            <WidgetPageContainer {...this.props} {...config}>
              {!_.isEmpty(messageToDisplay) && <NoDataInfoComponent />}
            </WidgetPageContainer>
          </Box>
        </Box>
      )
    }
  }
  const mapStateToProps = (state) => ({
    messageToDisplay: state.widgets.messages,
    widgetPreview: state.widgets.previewEnabled,
    widgetCallsOngoing: state.widgets.widgetCallsInProgress.length,
    widgetCallsError: state.widgets.widgetCallsWithErrors
  })

  const mapDispatchToProps = (dispatch) => ({
    resetCalls: () => dispatch(resetCall()),
    clearMessages: () => dispatch(clearWidgetMessage())
  })

  wrapper.displayName = `WithWidgets(${getDisplayName(WidgetPageContainer)})`
  wrapper.propTypes = {
    messageToDisplay: PropTypes.array,
    widgetPreview: PropTypes.bool,
    widgetCallsOngoing: PropTypes.number,
    widgetCallsError: PropTypes.number,
    resetCalls: PropTypes.func,
    clearMessages: PropTypes.func
  }
  wrapper.defaultProps = {
    messageToDisplay: [],
    widgetPreview: false,
    widgetCallsOngoing: 0,
    widgetCallsError: 0,
    resetCalls: undefined,
    clearMessages: undefined
  }

  return connect(mapStateToProps, mapDispatchToProps)(wrapper)
}
